// src/context/FormReducer.js - State reducer and management logic

import { ActionTypes, initialState, autoRestoreImages, revokeBlobUrl } from './FormTypes';

// Main reducer function
export const formReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.SET_PATIENT_DATA:
            return { ...state, patientData: action.payload, error: null };

        case ActionTypes.SET_CANALS_TRACED:
            return { ...state, canalsTraced: action.payload };

        case ActionTypes.SET_RADIOLOGICAL_IMPRESSIONS:
            return { ...state, radiologicalImpressions: action.payload };

        case ActionTypes.SET_OTHER_IMPRESSION:
            return { ...state, otherImpression: action.payload };

        case ActionTypes.SET_SINUS_VIEW_DATA:
            return { ...state, sinusViewData: { ...state.sinusViewData, ...action.payload } };

        case ActionTypes.SET_SAGITTAL_VIEW_DATA:
            return { ...state, sagittalViewData: { ...state.sagittalViewData, ...action.payload } };

        case ActionTypes.SET_PANORAMIC_DATA:
            return {
                ...state,
                panoramicViewData: { ...state.panoramicViewData, ...action.payload }
            };

        case ActionTypes.SET_DIMENSIONS_DATA:
            return {
                ...state,
                dimensionsData: { ...state.dimensionsData, ...action.payload }
            };

        case ActionTypes.SET_REGION_DATA:
            return {
                ...state,
                regionDetailsData: {
                    ...state.regionDetailsData,
                    [action.payload.regionNumber]: action.payload.data
                }
            };

        case ActionTypes.UPDATE_REGION_DATA:
            return {
                ...state,
                regionDetailsData: {
                    ...state.regionDetailsData,
                    [action.payload.regionNumber]: {
                        ...state.regionDetailsData[action.payload.regionNumber],
                        ...action.payload.updates
                    }
                }
            };

        case ActionTypes.REMOVE_REGION_DATA:
            const { [action.payload.regionNumber]: _, ...remainingRegionData } = state.regionDetailsData || {};
            const { [action.payload.regionNumber]: __, ...remainingRegionFormStates } = state.regionFormStates || {};
            return {
                ...state,
                regionDetailsData: remainingRegionData,
                regionFormStates: remainingRegionFormStates
            };

        case ActionTypes.SET_REPORT_TYPE:
            // Clear region data and selections when report type changes
            return {
                ...state,
                reportType: action.payload,
                regionDetailsData: {},
                regionDetailsPdfState: {
                    ...state.regionDetailsPdfState,
                    selected: [],
                    expanded: []
                }
            };

        case ActionTypes.SET_SELECTED_UNIT:
            return { ...state, selectedUnit: action.payload };

        case ActionTypes.SET_SELECTED_DOCTOR:
            return { ...state, selectedDoctor: action.payload };

        case ActionTypes.SET_SELECTED_FILE:
            return { ...state, selectedFile: action.payload };

        case ActionTypes.SET_IMPACTION_DATA:
            return {
                ...state,
                impactionData: action.payload
            };

        case ActionTypes.SET_CURRENT_TAB:
            return { ...state, currentTab: action.payload };

        case ActionTypes.SET_SIDEBAR_OPEN:
            return { ...state, isSidebarOpen: action.payload };

        case ActionTypes.TOGGLE_SIDEBAR:
            return { ...state, isSidebarOpen: !state.isSidebarOpen };

        case ActionTypes.SET_LOADING:
            return { ...state, loading: action.payload };

        case ActionTypes.SET_ERROR:
            return { ...state, error: action.payload };

        case ActionTypes.CLEAR_ERROR:
            return { ...state, error: null };

        case ActionTypes.SET_IS_RESTORING:
            return { ...state, isRestoring: action.payload };

        case ActionTypes.RESTORE_STATE:
            return { ...state, ...action.payload };

        case 'RESTORE_FROM_STORAGE':
            // Handle restoration from localStorage
            const { key, value } = action.payload;
            return { ...state, [key]: value };

        case ActionTypes.RESET_FORM:
            // Cleanup any existing blob URLs to prevent memory leaks
            const currentState = state;

            // Cleanup panoramic URLs
            if (currentState.panoramicViewUIState.preview1) {
                URL.revokeObjectURL(currentState.panoramicViewUIState.preview1);
            }
            if (currentState.panoramicViewUIState.preview2) {
                URL.revokeObjectURL(currentState.panoramicViewUIState.preview2);
            }

            // Cleanup dimensions URLs
            if (currentState.dimensionsPdfState.croppedImageUrl1) {
                URL.revokeObjectURL(currentState.dimensionsPdfState.croppedImageUrl1);
            }
            if (currentState.dimensionsPdfState.croppedImageUrl2) {
                URL.revokeObjectURL(currentState.dimensionsPdfState.croppedImageUrl2);
            }

            // Cleanup region URLs
            Object.values(currentState.regionFormStates).forEach(regionState => {
                if (regionState.croppedImageUrl1) {
                    URL.revokeObjectURL(regionState.croppedImageUrl1);
                }
                if (regionState.croppedImageUrl2) {
                    URL.revokeObjectURL(regionState.croppedImageUrl2);
                }
            });

            console.log('Form reset: All data cleared including blob URLs');

            return {
                ...initialState,
                isSidebarOpen: currentState.isSidebarOpen // Preserve sidebar state
            };

        case ActionTypes.UPDATE_DIMENSIONS_PDF_STATE:
            return {
                ...state,
                dimensionsPdfState: { ...state.dimensionsPdfState, ...action.payload }
            };

        case ActionTypes.UPDATE_REGION_DETAILS_PDF_STATE:
            return {
                ...state,
                regionDetailsPdfState: { ...state.regionDetailsPdfState, ...action.payload }
            };

        case ActionTypes.UPDATE_PANORAMIC_UI_STATE:
            return {
                ...state,
                panoramicViewUIState: { ...state.panoramicViewUIState, ...action.payload }
            };

        case ActionTypes.UPDATE_CANALS_UI_STATE:
            return {
                ...state,
                canalsUIState: { ...state.canalsUIState, ...action.payload }
            };

        case ActionTypes.UPDATE_REGION_FORM_STATE:
            return {
                ...state,
                regionFormStates: {
                    ...state.regionFormStates,
                    [action.payload.regionNumber]: {
                        ...state.regionFormStates[action.payload.regionNumber],
                        ...action.payload.updates
                    }
                }
            };

        case ActionTypes.AUTO_RESTORE_IMAGES:
            const { hasUpdates, updates } = autoRestoreImages(state);
            if (hasUpdates) {
                console.log('Auto-restored images in reducer');
                return { ...state, ...updates };
            }
            return state;

        case ActionTypes.CLEANUP_BLOB_URLS:
            const { regionNumber } = action.payload;
            const targetRegionState = state.regionFormStates[regionNumber];

            if (targetRegionState) {
                // Cleanup existing URLs
                revokeBlobUrl(targetRegionState.croppedImageUrl1);
                revokeBlobUrl(targetRegionState.croppedImageUrl2);

                return {
                    ...state,
                    regionFormStates: {
                        ...state.regionFormStates,
                        [regionNumber]: {
                            ...targetRegionState,
                            croppedImageUrl1: null,
                            croppedImageUrl2: null
                        }
                    }
                };
            }

            return state;

        default:
            return state;
    }
};

// Computed values helper functions
export const computedHelpers = {
    isFormValid: (state) => {
        return !!(state.patientData &&
            state.selectedUnit !== 'UNIT Number' &&
            state.selectedDoctor !== 'Doctor Name');
    },

    getConsolidatedReportData: (state) => {
        return {
            patientData: state.patientData,
            reportType: state.reportType,
            canalsTraced: state.canalsTraced,
            panoramicViewData: state.panoramicViewData,
            dimensionsData: state.dimensionsData,
            regionDetailsData: state.regionDetailsData
        };
    },

    hasUnsavedUIState: (state) => {
        return !!(
            state.dimensionsPdfState.convertedImages.length > 0 ||
            state.regionDetailsPdfState.convertedImages.length > 0 ||
            state.panoramicViewUIState.selectedComments.length > 0 ||
            Object.keys(state.regionFormStates).length > 0
        );
    },

    getImageStateDiagnostics: (state) => {
        const panoramic = {
            file1: !!state.panoramicViewData.panoramicFile1,
            file2: !!state.panoramicViewData.panoramicFile2,
            preview1: !!state.panoramicViewUIState.preview1,
            preview2: !!state.panoramicViewUIState.preview2,
            needsRestore: (!!state.panoramicViewData.panoramicFile1 && !state.panoramicViewUIState.preview1) ||
                (!!state.panoramicViewData.panoramicFile2 && !state.panoramicViewUIState.preview2)
        };

        const regions = {};
        Object.keys(state.regionFormStates).forEach(regionNumber => {
            const regionState = state.regionFormStates[regionNumber];
            regions[regionNumber] = {
                file1: !!regionState.croppedImageFile1,
                file2: !!regionState.croppedImageFile2,
                url1: !!regionState.croppedImageUrl1,
                url2: !!regionState.croppedImageUrl2,
                needsRestore: (!!regionState.croppedImageFile1 && !regionState.croppedImageUrl1) ||
                    (!!regionState.croppedImageFile2 && !regionState.croppedImageUrl2)
            };
        });

        return { panoramic, regions };
    }
};